import React, { useState, useCallback, useEffect, useRef } from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import ReactMicRecord from "react-mic-record";
import MicIcon from "@mui/icons-material/Mic";
import Countdown from "../../Student/Countdown";
import SilenceDetector from "../../../Pages/DemoTest/SilenceDetector";
import MicrophoneErrorPopup from "../../../Pages/DemoTest/MicrophoneErrorPopup";
import clickSound from "../../../Assets/sounds/click.mp3";
import axiosInstance from "../../../helpers/Axios";
import baseURL from "../../../config/config";
import { logReadingEvent } from "../../../helpers/readingEvents";

const ClassRecording = ({
  wsRef,
  unifiedSession,
  selectedTextbook,
  selectedStudent,
  setLoading,
  setApiFlag,
  storyId,
  currentStoryIndex,
  setLastActiveIndex,
  setIsRecordClicked,
  students,
  setIsRecording,
  isRecording,
  creditsRemaining = 0,
  refreshCredits,
  useCreditEnabled = true,
  socketres,
  setSocketres,
  showRecordingCountdown,
}) => {
  const [error, setError] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showwarning, setShowwarning] = useState(false);
  const [timer, setTimer] = useState();
  const [microphoneError, setMicrophoneError] = useState(null);
  const [showMicrophoneError, setShowMicrophoneError] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [numericCredits, setNumericCredits] = useState(0);
  const recordingStartTimeRef = useRef(null);
  // Add a ref to track if we're already showing a speech error
  const speechErrorShownRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const isWaitingForResponseRef = useRef(false);
  const responseTimeoutRef = useRef(null);

  console.log(
    "showRecordingCountdown in class recording : ",
    showRecordingCountdown,
  );
  const showSnack = (message, severity = "error") => {
    setSnack({ open: true, message, severity });
  };

  // DEBUG: Log when selected student changes
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug(
        "ClassRecording: selectedStudent changed to:",
        selectedStudent,
      );
    }
    if (selectedStudent) {
      setError(null);
    }
  }, [selectedStudent]);

  // DEBUG: Log credit props when they change
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("ClassRecording DEBUG - Credit props:", {
        creditsRemaining,
        useCreditEnabled,
        refreshCredits: typeof refreshCredits,
        numericCredits,
      });
    }
  }, [creditsRemaining, useCreditEnabled, numericCredits]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("ClassRecording: creditsRemaining =", creditsRemaining);
      console.debug("ClassRecording: useCreditEnabled =", useCreditEnabled);
    }
  }, [creditsRemaining, useCreditEnabled]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug(
        "Credits updated:",
        numericCredits,
        "Has credits:",
        hasCredits(),
      );
    }
  }, [numericCredits]);

  const timerIntervalRef = useRef(null);
  const submissionTimerRef = useRef(null);
  const silentTimeRef = useRef(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioStreamRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const waveformAnimationRef = useRef(null);

  // Add WebSocket message listener ref
  const wsMessageListenerRef = useRef(null);

  const playClickSound = useCallback(() => {
    const audio = new Audio(clickSound);
    audio
      .play()
      .catch((err) => console.error("Error playing click sound:", err));
  }, []);

  // Draws the waveform straight from analyserRef.current (already live
  // the instant startRecording() grants mic access) instead of waiting
  // on ReactMicRecord's own internal audio init, which is what causes
  // the multi-second delay on slower/older smartboards.
  const drawWaveform = useCallback(() => {
    const canvas = waveformCanvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext("2d");
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const render = () => {
      waveformAnimationRef.current = requestAnimationFrame(render);
      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#ff4b00";
      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }

      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };

    render();
  }, []);

  useEffect(() => {
    if (isRecording) {
      drawWaveform();
    } else if (waveformAnimationRef.current) {
      cancelAnimationFrame(waveformAnimationRef.current);
      waveformAnimationRef.current = null;
    }

    return () => {
      if (waveformAnimationRef.current) {
        cancelAnimationFrame(waveformAnimationRef.current);
        waveformAnimationRef.current = null;
      }
    };
  }, [isRecording, drawWaveform]);

  const getRemainingAttempts = useCallback(() => {
    if (!selectedStudent || !students || !students.length) return 1;

    const studentInfo = students.find(
      (student) =>
        student.user?.name === selectedStudent ||
        student.id === selectedStudent,
    );

    return studentInfo?.remaining_count || 0;
  }, [selectedStudent, students]);

  const getNumericCredits = useCallback(() => {
    if (!useCreditEnabled) {
      return Infinity;
    }

    if (creditsRemaining == null) return 0;

    if (typeof creditsRemaining === "number") {
      return creditsRemaining;
    }

    if (creditsRemaining === Infinity) {
      return Infinity;
    }

    if (typeof creditsRemaining === "string") {
      const sanitized = creditsRemaining.replace(/,/g, "").trim();
      const parsed = Number(sanitized);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    if (typeof creditsRemaining === "object") {
      if (Array.isArray(creditsRemaining)) {
        return creditsRemaining.reduce((s, v) => s + (Number(v) || 0), 0);
      }
      if (Array.isArray(creditsRemaining.wallets)) {
        return creditsRemaining.wallets.reduce(
          (s, w) => s + (Number(w?.remaining_credits) || 0),
          0,
        );
      }
      if (creditsRemaining.remaining_credits !== undefined) {
        const p = Number(creditsRemaining.remaining_credits);
        return Number.isNaN(p) ? 0 : p;
      }
    }

    return 0;
  }, [creditsRemaining, useCreditEnabled]);

  const fetchCreditsDirect = useCallback(async () => {
    try {
      console.log("🔄 DEBUG: Calling fetchCreditsDirect API...");
      const res = await axiosInstance.get(`${baseURL}/api/classroom/wallet/`);
      console.log("🔄 DEBUG: fetchCreditsDirect response:", res?.data);

      if (res?.data?.status === "success" && res.data.data) {
        const data = res.data.data;

        if (data?.use_credit === false) {
          console.log("🔄 DEBUG: use_credit is false, returning Infinity");
          return Infinity;
        }

        if (Array.isArray(data.wallets)) {
          const total = data.wallets.reduce(
            (s, w) => s + (Number(w?.remaining_credits) || 0),
            0,
          );
          console.log("🔄 DEBUG: Total credits from wallets:", total);
          return total;
        }
        if (data.remaining_credits !== undefined) {
          const p = Number(data.remaining_credits);
          const result = Number.isNaN(p) ? 0 : p;
          console.log("🔄 DEBUG: Credits from remaining_credits:", result);
          return result;
        }
      }
    } catch (e) {
      console.warn("🔄 DEBUG: fetchCreditsDirect failed:", e?.message || e);
    }
    return 0;
  }, []);

  useEffect(() => {
    const cur = getNumericCredits();
    console.log(
      "💰 DEBUG: Setting numericCredits from getNumericCredits():",
      cur,
    );
    setNumericCredits(cur);

    if (useCreditEnabled && cur <= 0) {
      console.log(
        "💰 DEBUG: Credits are 0 or negative, checking live credits...",
      );
      (async () => {
        const live = await fetchCreditsDirect();
        console.log("💰 DEBUG: Live credits from API:", live);
        if (live > 0 && live !== Infinity) {
          console.log("💰 DEBUG: Setting credits to live value:", live);
          setNumericCredits(live);
          if (typeof refreshCredits === "function") {
            try {
              console.log(
                "💰 DEBUG: Calling refreshCredits during mount check...",
              );
              await refreshCredits();
              console.log(
                "💰 DEBUG: refreshCredits completed during mount check",
              );
            } catch (e) {
              console.warn(
                "💰 DEBUG: refreshCredits during mount check failed:",
                e?.message || e,
              );
            }
          }
        }
      })();
    }
  }, [
    creditsRemaining,
    getNumericCredits,
    fetchCreditsDirect,
    refreshCredits,
    useCreditEnabled,
  ]);

  const hasCredits = useCallback(() => {
    const num = numericCredits;

    if (!useCreditEnabled || num === Infinity) return true;

    return num > 0;
  }, [numericCredits, useCreditEnabled]);

  const retryWalletRefresh = useCallback(
    async (maxRetries = 3) => {
      console.log(
        "🔄 DEBUG: Starting retryWalletRefresh with maxRetries:",
        maxRetries,
      );
      for (let i = 0; i < maxRetries; i++) {
        try {
          if (typeof refreshCredits === "function") {
            console.log(
              `🔄 DEBUG: Wallet refresh attempt ${i + 1}/${maxRetries}...`,
            );
            await refreshCredits();
            console.log(`🔄 DEBUG: Wallet refresh attempt ${i + 1} succeeded`);
            return true;
          }
        } catch (error) {
          console.warn(
            `🔄 DEBUG: Wallet refresh attempt ${i + 1} failed:`,
            error,
          );
          if (i < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
          }
        }
      }
      console.log("🔄 DEBUG: All wallet refresh attempts failed");
      return false;
    },
    [refreshCredits],
  );

  const startRecording = useCallback(() => {
    console.log("🎤 DEBUG: startRecording function called");

    // Get effective session ID
    const effectiveSessionId = unifiedSession.id;

    if (!effectiveSessionId) {
      console.error("🎤 ERROR: No session ID available to start recording!");
      setError("Session not properly initialized");
      setIsRecording(false);
      return;
    }

    console.log(
      "🎤 DEBUG: Using session ID for start_recording:",
      effectiveSessionId,
    );

    setLastActiveIndex(currentStoryIndex);

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        console.log("🎤 DEBUG: Microphone access granted");
        audioStreamRef.current = stream;

        const audioContext = new (
          window.AudioContext || window.webkitAudioContext
        )();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        recordingStartTimeRef.current = Date.now();

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try {
            // Send start_recording message with session ID
            const startPayload = {
              type: "start_recording",
              session_id: effectiveSessionId, // Use the effective session ID
            };

            console.log(
              "🎤 DEBUG: Sending start_recording payload:",
              startPayload,
            );
            wsRef.current.send(JSON.stringify(startPayload));

            isSubmittingRef.current = false; // Reset on start
            setIsRecording(true);
            setError(null);
            // Recording is now genuinely live — safe to dismiss the "Go"
            // overlay so students only start speaking once audio is
            // actually being captured.
            setShowCountdown(false);
            console.log("🎤 DEBUG: Recording started successfully");
          } catch (err) {
            console.error("🎤 DEBUG: Error starting recording:", err);
            setError("Failed to start recording");
            setShowCountdown(false);
          }
        } else {
          console.error("🎤 DEBUG: WebSocket not connected");
          setError("Connection error. Please try again.");
          setIsRecording(false);
          setShowCountdown(false);
        }
      })
      .catch((err) => {
        console.error("🎤 DEBUG: Microphone access error:", err);
        logReadingEvent(unifiedSession.id, "mic_error", { name: err?.name });
        setIsRecording(false);
        setShowCountdown(false);
        setError("No speech detected in recording please try again");

        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setMicrophoneError("browser_permission");
          setShowMicrophoneError(true);
          setError(null);
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          setMicrophoneError("no_device");
          setShowMicrophoneError(true);
          setError(null);
        } else if (
          err.name === "NotReadableError" ||
          err.name === "TrackStartError"
        ) {
          setMicrophoneError("system_permission");
          setShowMicrophoneError(true);
          setError(null);
        }
      });
  }, [
    wsRef,
    currentStoryIndex,
    setLastActiveIndex,
    setIsRecording,
    unifiedSession,
    setShowCountdown,
  ]);
  const isButtonDisabled = useCallback(() => {
    const disabled =
      !isRecording &&
      (getRemainingAttempts() <= 0 || (useCreditEnabled && !hasCredits()));
    if (process.env.NODE_ENV !== "production") {
      console.debug("ClassRecording: isButtonDisabled =", disabled, {
        isRecording,
        remainingAttempts: getRemainingAttempts(),
        hasCredits: hasCredits(),
        useCreditEnabled,
      });
    }
    return disabled;
  }, [isRecording, getRemainingAttempts, hasCredits, useCreditEnabled]);

  const shouldBreath = useCallback(() => {
    if (!useCreditEnabled) {
      return !isRecording && getRemainingAttempts() > 0;
    }

    return !isRecording && getRemainingAttempts() > 0 && hasCredits();
  }, [isRecording, getRemainingAttempts, hasCredits, useCreditEnabled]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes breathing {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      .breathing-button { animation: breathing 2s ease-in-out infinite; }
      
      .record-wave-wrapper {
        width: 100%;
        max-width: 320px;
        height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      }
      
      .sound-wave { width: 100%; height: 100%; }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const initiateRecording = useCallback(async () => {
    console.log("🎤 DEBUG: initiateRecording called");
    console.log("🎤 DEBUG: unifiedSession:", unifiedSession);

    speechErrorShownRef.current = false; // Reset speech error flag

    if (!selectedStudent) {
      logReadingEvent(unifiedSession.id, "record_blocked", {
        reason: "no_student",
        reading_content_id: storyId,
      });
      setError("Please select a student before recording");
      return;
    }

    setError(null);

    if (getRemainingAttempts() <= 0) {
      logReadingEvent(unifiedSession.id, "record_blocked", {
        reason: "no_attempts_left",
        student_id: selectedStudent,
        reading_content_id: storyId,
      });
      setError("No attempts left for this student");
      return;
    }

    if (useCreditEnabled && !hasCredits()) {
      console.log("💰 DEBUG: No credits detected, trying to refresh...");
      if (typeof refreshCredits === "function") {
        try {
          console.log("💰 DEBUG: Calling refreshCredits before recording...");
          await refreshCredits();
          console.log("💰 DEBUG: refreshCredits completed before recording");
        } catch (e) {
          console.warn(
            "💰 DEBUG: refreshCredits failed before recording:",
            e?.message || e,
          );
        }
      }

      const liveCredits = await fetchCreditsDirect();
      console.log("💰 DEBUG: Live credits check result:", liveCredits);
      if (liveCredits <= 0 && liveCredits !== Infinity) {
        logReadingEvent(unifiedSession.id, "record_blocked", {
          reason: "no_credits",
          student_id: selectedStudent,
          reading_content_id: storyId,
        });
        setError("No credits left for this session. Please top up.");
        return;
      }
    }

    if (unifiedSession.type === "textbook" && !selectedTextbook && !storyId) {
      setError("Please select a textbook content before recording");
      return;
    }

    // Check if we have a valid session ID
    const effectiveSessionId = unifiedSession.id;
    if (!effectiveSessionId) {
      console.error("🎤 ERROR: No session ID available!");
      setError(
        "Session not properly initialized. Please try refreshing the page.",
      );
      return;
    }

    console.log(
      "🎤 DEBUG: Using session ID for recording:",
      effectiveSessionId,
    );

    logReadingEvent(effectiveSessionId, "record_clicked", {
      student_id: selectedStudent,
      reading_content_id: storyId,
    });
    setIsRecordClicked((prev) => !prev);

    // Always show the overlay.
    // Countdown component decides whether to display:
    // 3 → 2 → 1 → Go
    // or just Go for 1 second.
    setShowCountdown(true);
  }, [
    selectedStudent,
    selectedTextbook,
    unifiedSession,
    setIsRecordClicked,
    getRemainingAttempts,
    hasCredits,
    refreshCredits,
    fetchCreditsDirect,
    useCreditEnabled,
    storyId,
    showRecordingCountdown,
    startRecording,
  ]);

  // Extract success handling to a persistent function
  const handleSuccessGlobal = useCallback(async () => {
    isWaitingForResponseRef.current = false;
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }

    if (useCreditEnabled && typeof refreshCredits === "function") {
      try {
        console.log(
          "💰 DEBUG: Calling refreshCredits after socketres update...",
        );
        await refreshCredits();
        setApiFlag((prev) => !prev);
        console.log("💰 DEBUG: refreshCredits completed");
        showSnack("Recording submitted successfully!", "success");
      } catch (e) {
        console.warn("💰 DEBUG: Wallet refresh failed:", e);
        showSnack("Recording submitted but credit update failed", "warning");
      }
    } else {
      showSnack("Recording submitted successfully!", "success");
    }

    setLoading(false);
    setIsRecording(false);
    isSubmittingRef.current = false; // Guard is finally released
  }, [
    refreshCredits,
    setApiFlag,
    setLoading,
    setIsRecording,
    useCreditEnabled,
  ]);

  // Monitor socketres for completion
  useEffect(() => {
    if (socketres && isWaitingForResponseRef.current) {
      console.log("🎤 DEBUG: socketres detected, handling success");
      handleSuccessGlobal();
    }
  }, [socketres, handleSuccessGlobal]);

  const handleStopRecording = useCallback(
    async (isAutoStop = false) => {
      if (isSubmittingRef.current) {
        console.log(
          "🛑 DEBUG: handleStopRecording already in progress, skipping",
        );
        return;
      }

      console.log(
        "🛑 DEBUG: handleStopRecording called, isAutoStop:",
        isAutoStop,
      );
      isSubmittingRef.current = true;

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      setLoading(true);
      setShowwarning(false);
      setIsRecording(false);
      isWaitingForResponseRef.current = true;

      try {
        const recordingDuration = recordingStartTimeRef.current
          ? (Date.now() - recordingStartTimeRef.current) / 1000
          : 0;

        console.log("🛑 DEBUG: Recording duration:", recordingDuration);
        recordingStartTimeRef.current = null;

        let contentId;
        if (unifiedSession.type === "textbook" && storyId) {
          contentId = storyId;
        } else if (unifiedSession.type === "textbook" && selectedTextbook?.id) {
          contentId = selectedTextbook.id;
        } else {
          contentId = storyId;
        }

        // Get effective session ID
        const effectiveSessionId = unifiedSession.id;

        if (!effectiveSessionId) {
          console.error(
            "🛑 ERROR: No session ID available for stop_recording!",
          );
          throw new Error("Session ID missing");
        }

        console.log(
          "🛑 DEBUG: Using session ID for stop_recording:",
          effectiveSessionId,
        );

        const payload = {
          type: "stop_recording",
          session_id: Number(effectiveSessionId), // Use the effective session ID
          student_id: Number(selectedStudent), // Fallback naming
          student: Number(selectedStudent),
          reading_content_id: Number(contentId),
          story_id: Number(contentId), // Fallback naming for passage sessions
          standard: Number(unifiedSession.standard), // Added context
          section: Number(unifiedSession.section), // Added context
          is_submitted: true,
          audio_duration: parseFloat(recordingDuration.toFixed(2)),
        };

        console.log("🛑 DEBUG: Sending stop_recording payload:", payload);
        logReadingEvent(effectiveSessionId, "record_stopped_client", {
          student_id: payload.student_id,
          reading_content_id: payload.reading_content_id,
          auto_stop: !!isAutoStop,
          duration_seconds: payload.audio_duration,
        });

        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          throw new Error("WebSocket not connected");
        }

        if (typeof setSocketres === "function") setSocketres(null);
        wsRef.current.send(JSON.stringify(payload));

        // Set a timeout for generic error if no response (increased to 15s)
        responseTimeoutRef.current = setTimeout(() => {
          if (isWaitingForResponseRef.current) {
            isWaitingForResponseRef.current = false;
            setLoading(false);
            setIsRecording(false);
            isSubmittingRef.current = false;
            showSnack(
              "Recording timeout: No response from server. Please check your connection.",
              "error",
            );
          }
        }, 15000);

        // isSubmittingRef.current used to be false here, now moved to handleSuccessGlobal/timeout
      } catch (err) {
        console.error("🛑 DEBUG: WebSocket submission failed:", err);

        isSubmittingRef.current = false;
        isWaitingForResponseRef.current = false;
        setLoading(false);
        setIsRecording(false);

        // Provide more specific error feedback
        const errorMsg =
          err.message === "WebSocket not connected"
            ? "WebSocket connection lost. Reconnecting..."
            : err.message === "Session ID missing"
              ? "Session expired or not initialized. Please re-enter the class."
              : "Something went wrong during submission. Please try again.";

        showSnack(errorMsg, "error");
        recordingStartTimeRef.current = null;
      } finally {
        if (isAutoStop) {
          showSnack(
            "Recording stopped automatically as no voice was detected for 8 seconds.",
            "warning",
          );
        }
      }
    },
    [
      wsRef,
      unifiedSession,
      selectedTextbook,
      selectedStudent,
      setLoading,
      setIsRecording,
      numericCredits,
      fetchCreditsDirect,
      showSnack,
    ],
  );

  const handleButtonClick = useCallback(() => {
    console.log("🖱️ DEBUG: handleButtonClick, isRecording:", isRecording);
    playClickSound();
    if (isRecording) {
      handleStopRecording();
    } else {
      initiateRecording();
    }
  }, [isRecording, handleStopRecording, initiateRecording, playClickSound]);

  useEffect(() => {
    if (showwarning) {
      setTimer(5);
      submissionTimerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(submissionTimerRef.current);
            handleStopRecording();
            setShowwarning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (submissionTimerRef.current) {
        clearInterval(submissionTimerRef.current);
      }
    }

    return () => {
      if (submissionTimerRef.current) {
        clearInterval(submissionTimerRef.current);
      }
    };
  }, [showwarning, handleStopRecording]);

  const handleCountdownEnd = useCallback(() => {
    console.log("⏱️ DEBUG: Countdown ended, starting recording...");
    // Don't hide the "Go" overlay yet — keep it up until the microphone is
    // actually live and recording has truly begun. startRecording() will
    // hide it once getUserMedia + WS start succeed (or on error), so any
    // device-dependent delay is absorbed by the overlay instead of the
    // student thinking they should already be speaking.
    startRecording();
  }, [startRecording]);

  useEffect(() => {
    return () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (submissionTimerRef.current) {
        clearInterval(submissionTimerRef.current);
        submissionTimerRef.current = null;
      }
      if (waveformAnimationRef.current) {
        cancelAnimationFrame(waveformAnimationRef.current);
        waveformAnimationRef.current = null;
      }
      // Clean up WebSocket listener
      if (wsRef.current && wsMessageListenerRef.current) {
        wsRef.current.removeEventListener(
          "message",
          wsMessageListenerRef.current,
        );
        wsMessageListenerRef.current = null;
      }
    };
  }, []);

  const getButtonText = () => {
    if (isRecording) return "Stop";

    if (!useCreditEnabled) {
      return getRemainingAttempts() <= 0 ? "Attempts over" : "Record";
    }

    if (!hasCredits()) return "No credits";
    return getRemainingAttempts() <= 0 ? "Attempts over" : "Record";
  };

  return (
    <Box className="flex flex-col items-center gap-4 mt-5">
      {showCountdown && (
        <Countdown
          showFullCountdown={showRecordingCountdown}
          onCountdownEnd={handleCountdownEnd}
        />
      )}

      <Box className="flex flex-row items-center justify-center gap-6 w-full px-8">
        <Box className="flex-1 max-w-xs">
          {isRecording ? (
            <div className="record-wave-wrapper">
              <canvas
                ref={waveformCanvasRef}
                width={320}
                height={90}
                className="sound-wave"
              />
              <div style={{ display: "none" }}>
                <ReactMicRecord
                  record={isRecording}
                  onStop={handleStopRecording}
                  onData={(recordedBlob) => {
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(recordedBlob);
                    }
                  }}
                  strokeColor="#ff4b00"
                  backgroundColor="#fff"
                  className="sound-wave"
                />
              </div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center p-4 border border-red-300 rounded bg-red-50">
              {error}
            </div>
          ) : null}
        </Box>

        <Button
          onClick={handleButtonClick}
          startIcon={<MicIcon />}
          disabled={isButtonDisabled()}
          className={shouldBreath() ? "breathing-button" : ""}
          sx={{
            backgroundColor: isRecording
              ? "#f97316"
              : isButtonDisabled()
                ? "#94a3b8"
                : "#f97316",
            color: "white",
            padding: "12px 32px",
            fontSize: "13px",
            fontWeight: "700",
            borderRadius: "12px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: isButtonDisabled()
              ? "none"
              : "0 8px 20px -6px rgba(249,115,22,0.4)",
            "&:hover": {
              backgroundColor: isRecording
                ? "#ea580c"
                : isButtonDisabled()
                  ? "#94a3b8"
                  : "#ea580c",
              boxShadow: isButtonDisabled()
                ? "none"
                : "0 12px 25px -8px rgba(249,115,22,0.5)",
              transform: isButtonDisabled()
                ? "none"
                : "translateY(-2px) scale(1.02)",
            },
            "&:disabled": {
              backgroundColor: "#E2E8F0",
              color: "#94A3B8",
            },
          }}
        >
          {getButtonText()}
        </Button>
      </Box>

      <SilenceDetector
        isRecording={isRecording}
        wsRef={wsRef}
        onStopRecording={() => handleStopRecording(true)}
        silenceThreshold={0.1}
        silenceTimeout={5000}
        countdownDuration={3}
        externalAnalyser={analyserRef.current}
      />

      <MicrophoneErrorPopup
        isOpen={showMicrophoneError}
        onClose={() => setShowMicrophoneError(false)}
        errorType={microphoneError}
      />

      {showwarning && (
        <Snackbar
          open={showwarning}
          message={`Recording submits in ${timer} seconds`}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          action={
            <Button
              onClick={() => {
                setShowwarning(false);
                if (submissionTimerRef.current) {
                  clearInterval(submissionTimerRef.current);
                }
                silentTimeRef.current = 0;
              }}
              sx={{
                color: "white",
                backgroundColor: "#DC2626",
                "&:hover": { backgroundColor: "#B91C1C" },
                marginLeft: "8px",
              }}
            >
              Skip
            </Button>
          }
          sx={{
            "& .MuiSnackbarContent-root": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            },
          }}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassRecording;
